import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { RelevamientoNotificacion } from '../services/relevamientoService';
import { useRelevamientoNotifications } from '../hooks/useRelevamientoNotifications';
import {
  DROPDOWN_VISIBLE_COUNT,
  notificationLocation,
  notificationTitle,
  relativeTime,
} from '../utils/relevamientoNotifications';

const NotificationsDropdown: React.FC = () => {
  const navigate = useNavigate();
  const {
    items,
    loading,
    permissionsLoading,
    canViewRelevamientos,
    unreadCount,
    isRead,
    markRead,
    refresh,
  } = useRelevamientoNotifications();
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  if (permissionsLoading || !canViewRelevamientos) {
    return null;
  }

  const visibleItems = items.slice(0, DROPDOWN_VISIBLE_COUNT);

  const handleOpenToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) refresh();
  };

  const handleItemClick = (item: RelevamientoNotificacion) => {
    markRead([item.id]);
    setOpen(false);
    navigate(`/relevamientos/${item.id}`);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={handleOpenToggle}
        className="relative p-2 text-gray-600 hover:text-primary-500 hover:bg-gray-100 rounded-lg transition-all duration-200 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-800"
        aria-label="Notificaciones"
      >
        <BellIcon className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-accent-400 text-primary-600 text-xs font-bold rounded-full min-w-[1.25rem] h-5 px-1 flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.15 }}
          className="absolute right-0 mt-2 w-80 bg-white dark:!bg-gray-800 rounded-lg shadow-strong border border-gray-200 dark:border-gray-700 py-1 z-50 notifications-dropdown"
        >
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-600">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Notificaciones</h3>
          </div>

          <div className="max-h-72 overflow-y-auto">
            {loading && visibleItems.length === 0 ? (
              <div className="p-4 space-y-3 animate-pulse">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded" />
                ))}
              </div>
            ) : visibleItems.length === 0 ? (
              <p className="px-4 py-8 text-sm text-center text-gray-500 dark:text-gray-400">
                No hay relevamientos pendientes
              </p>
            ) : (
              visibleItems.map((item) => {
                const read = isRead(item.id);
                const location = notificationLocation(item);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleItemClick(item)}
                    className={`w-full text-left p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      read ? 'bg-white dark:!bg-gray-800' : 'bg-accent-50 dark:!bg-accent-900/20'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm text-gray-900 dark:text-gray-100 font-medium line-clamp-2">
                        {notificationTitle(item)}
                      </p>
                      {!read && (
                        <span className="mt-1.5 w-2 h-2 rounded-full bg-primary-500 shrink-0" />
                      )}
                    </div>
                    {location && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                        {location}
                      </p>
                    )}
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {item.estadoNombre || 'Abierto'}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {relativeTime(item.fechaCreacion || item.fecha)}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-600">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/notificaciones');
              }}
              className="w-full text-center text-sm text-primary-600 dark:text-primary-400 hover:underline py-1"
            >
              Mostrar más
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default NotificationsDropdown;
